import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, X, Users } from 'lucide-react';
import { createGroup } from '../../services/groupService';
import Toast from '../Toast/Toast';
import { GroupType } from '../../types/groups';
import type { CreateGroupRequest, CreateGroupMember, GroupDto } from '../../types/groups';
import type { UserProfile } from '../../types/UserProfile';
import { useAuth } from '../../hooks/useAuth';
import { useFieldLabel } from '../../hooks/useFieldLabel';

export interface CreateGroupModalProps {
  open: boolean;
  selectedUsers: UserProfile[];
  onClose: () => void;
  onCreated?: (group: GroupDto) => void;
}

interface FormErrors {
  groupName?: string;
  description?: string;
  members?: string;
  api?: string;
}

const groupTypeOptions: Array<{ value: GroupType; title: string; description: string }> = [
  {
    value: GroupType.Project,
    title: GroupType.Project,
    description: 'Coordinate work for a specific project',
  },
  {
    value: GroupType.CustomerCall,
    title: GroupType.CustomerCall,
    description: 'Discuss customer meetings and follow-ups',
  },
  {
    value: GroupType.Problem,
    title: GroupType.Problem,
    description: 'Track an issue, risk, or blocker',
  },
];



function getUserId(user: UserProfile) {
  return user.userId || '';
}

function getUserName(user: UserProfile) {
  const flatUser = user as UserProfile & { firstName?: string; lastName?: string; fullName?: string; name?: string };
  const name =
    flatUser.fullName ||
    flatUser.name ||
    [flatUser.firstName || user.personalInfo?.firstName, flatUser.lastName || user.personalInfo?.lastName]
      .filter(Boolean)
      .join(' ');

  return name.trim() || 'Unknown User';
}

function getProfilePhoto(user: UserProfile) {
  const flatUser = user as UserProfile & { profileImageUrl?: string; photoUrl?: string; profilePhotoUrl?: string };
  return flatUser.profileImageUrl || flatUser.profilePhotoUrl || flatUser.photoUrl || user.personalInfo?.profileImage || '';
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function isCreator(userId: string, currentUserId?: string): boolean {
  return userId === currentUserId;
}

export default function CreateGroupModal({ open, selectedUsers, onClose, onCreated }: CreateGroupModalProps) {
  const queryClient = useQueryClient();
  const { data: authData } = useAuth();
  const currentUserId = authData?.userId;

  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [groupType, setGroupType] = useState<GroupType>(GroupType.Project);
  const [selectedMembers, setSelectedMembers] = useState<UserProfile[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
 const listLabel = useFieldLabel('MENU_LIST', 'List');


  const resetForm = () => {
    setGroupName('');
    setDescription('');
    setGroupType(GroupType.Project);
    setSelectedMembers([]);
    setErrors({});
    setToast(null);
  };

  useEffect(() => {
    if (open) {
      setGroupName('');
      setDescription('');
      setGroupType(GroupType.Project);
      setSelectedMembers(selectedUsers);
      setErrors({});
      setToast(null);
    }
  }, [open, selectedUsers]);

  // Build members array with creator always as admin and selected users as members
  const members = useMemo(() => {
    if (!currentUserId) return [];

    const membersList: CreateGroupMember[] = [
      {
        userId: currentUserId,
        isAdmin: true,
      },
    ];

    // Add selected users (excluding creator if somehow selected)
    selectedMembers.forEach((user) => {
      const userId = getUserId(user);
      if (userId && userId !== currentUserId) {
        membersList.push({
          userId,
          isAdmin: false,
        });
      }
    });

    return membersList;
  }, [currentUserId, selectedMembers]);

  // Check for duplicate users in the members list
  const hasDuplicateUsers = useMemo(() => {
    const userIds = new Set(members.map((m) => m.userId));
    return userIds.size !== members.length;
  }, [members]);

  const createGroupMutation = useMutation({
    mutationFn: () => {
      const payload: CreateGroupRequest = {
        groupName: groupName.trim(),
        description: description.trim(),
        groupType,
        members,
      };

      return createGroup(payload);
    },
    onSuccess: (group) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      onCreated?.(group);
      resetForm();
      onClose();
      setToast({ message: `${listLabel} created successfully.`, type: 'success' });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Unable to create list. Please try again.';
      setErrors((current) => ({
        ...current,
        api: message,
      }));
      setToast({ message, type: 'error' });
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !createGroupMutation.isPending) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, createGroupMutation.isPending]);

  if (!open) {
    return null;
  }

  const removeMember = (userId: string) => {
    // Prevent removing the current user (creator)
    if (userId === currentUserId) {
      return;
    }
    setSelectedMembers((current) => current.filter((member) => getUserId(member) !== userId));
    setErrors((current) => ({ ...current, members: undefined }));
  };

  const validateForm = () => {
    const nextErrors: FormErrors = {};

    if (!groupName.trim()) {
      nextErrors.groupName = 'List Name is required.';
    }

    if (!description.trim()) {
      nextErrors.description = 'Description is required.';
    }

    // A list cannot contain only its creator; require at least one other member.
    if (members.length < 2) {
      nextErrors.members = 'Please add at least one other member before creating the list.';
    }

    if (hasDuplicateUsers) {
      nextErrors.members = 'Duplicate users detected. Please remove duplicates.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    createGroupMutation.mutate();
  };

  
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4 py-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !createGroupMutation.isPending) {
          onClose();
        }
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="mb-1 text-xl font-bold text-gray-900">Create New {listLabel}</h2>
            <p className="mb-0 text-sm text-gray-500">{members.length} {members.length === 1 ? 'member' : 'members'} selected</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
            aria-label="Close create list modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-800" htmlFor="groupName">
                {listLabel} Name
              </label>
              <input
                id="groupName"
                value={groupName}
                onChange={(event) => {
                  setGroupName(event.target.value);
                  setErrors((current) => ({ ...current, groupName: undefined, api: undefined }));
                }}
                maxLength={100}
                placeholder={`Enter ${listLabel.toLowerCase()} name`}
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
              <div className="mt-1 flex justify-between gap-3">
                {errors.groupName ? <p className="mb-0 text-xs text-red-600">{errors.groupName}</p> : <span />}
                <span className="text-xs text-gray-400">{groupName.length}/100</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-800" htmlFor="groupDescription">
                Description
              </label>
              <textarea
                id="groupDescription"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setErrors((current) => ({ ...current, description: undefined, api: undefined }));
                }}
                maxLength={1000}
                rows={4}
                placeholder={`Describe the purpose of this ${listLabel}`}
                className="w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
              <div className="mt-1 flex justify-between gap-3">
                {errors.description ? <p className="mb-0 text-xs text-red-600">{errors.description}</p> : <span />}
                <span className="text-xs text-gray-400">{description.length}/1000</span>
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <label className="block text-sm font-semibold text-gray-800">Members</label>
                <span className="text-xs font-medium text-gray-500">{members.length} selected</span>
              </div>
              <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                {members.length > 0 ? (
                  members.map((member) => {
                    // Find the user profile for this member
                    const userProfile = member.userId === currentUserId
                      ? selectedMembers[0] // Placeholder for creator user
                      : selectedMembers.find((u) => getUserId(u) === member.userId);

                    // Build the name from available data
                    let name = 'Unknown User';
                    if (member.userId === currentUserId) {
                      // Try to get creator name from auth data or fallback
                      name = 'You'; // Simplified, could extend with auth name
                    } else if (userProfile) {
                      name = getUserName(userProfile);
                    }

                    const photo = userProfile ? getProfilePhoto(userProfile) : '';
                    const badge = member.isAdmin ? 'Admin' : 'Member';
                    const isCurrentUser = member.userId === currentUserId;

                    return (
                      <div key={member.userId} className="flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 shadow-sm">
                        {photo ? (
                          <img src={photo} alt={name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                            {getInitials(name) || <Users className="h-4 w-4" />}
                          </span>
                        )}
                        <div className="flex items-center gap-1">
                          <span className="max-w-[120px] truncate text-sm font-medium text-gray-800">{name}</span>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            member.isAdmin
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {badge}
                          </span>
                        </div>
                        {!isCurrentUser && (
                          <button
                            type="button"
                            onClick={() => removeMember(member.userId)}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-900"
                            aria-label={`Remove ${name}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="mb-0 text-sm text-gray-500">No members selected.</p>
                )}
              </div>
              {errors.members ? <p className="mb-0 mt-1 text-xs text-red-600">{errors.members}</p> : null}
            </div>

            <div>
              <label className="mb-3 block text-sm font-semibold text-gray-800">
                {listLabel} Type <span className="text-red-600">*</span>
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                {groupTypeOptions.map((option) => {
                  const isSelected = groupType === option.value;

                  return (
                    <label
                      key={option.value}
                      className={`cursor-pointer rounded-lg border p-4 transition ${
                        isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20' : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="groupType"
                          value={option.value}
                          checked={isSelected}
                          onChange={() => setGroupType(option.value)}
                          className="mt-1 accent-blue-600"
                        />
                        <div>
                          <p className="mb-1 text-sm font-semibold text-gray-900">{option.title}</p>
                          <p className="mb-0 text-xs leading-5 text-gray-500">{option.description}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {errors.api ? <p className="mb-0 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errors.api}</p> : null}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={createGroupMutation.isPending}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-gray-200 px-5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createGroupMutation.isPending}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {createGroupMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                 `Create ${listLabel}`
              )}
            </button>
          </div>
        </form>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
